import wasmUrl from './balancer.wasm?url';

export type Kind = 'live' | 'file';
export type Lang = 'multilingual' | 'english';
export type Place = { at: 'waiting' } | { at: 'fly' } | { at: 'pod'; pod: number };
export type PodState = 'creating' | 'booting' | 'ready' | 'draining' | 'stopped' | 'gone';

export interface GpuType { name: string; perH: number; slots: number; diskGb: number; gpus?: string[] }
export interface Gpu { name: string; runpodId: string; aliases: string[]; memoryGb: number; deviceMb: number; sm: string; perH: number; slots: number; slotsMeasured: boolean }
export interface PodMemory { engineMb: number; fixedMb: number; marginMb: number; poolMb: number; voiceMb: number }
export const BUNDLE_SM = ['8.0', '8.6', '8.9', '9.0'];
export const runnable = (g: Gpu) => BUNDLE_SM.includes(g.sm);
export const maxSlots = (m: PodMemory, deviceMb: number) => Math.min(9, Math.floor(Math.max(0, deviceMb - (m.fixedMb + m.marginMb + m.poolMb + m.voiceMb)) / Math.max(1, m.engineMb)));
export type Strategy = 'demand' | 'gpuFirst';
export const STRATEGIES: { value: Strategy; label: string }[] = [
  { value: 'demand', label: 'Pods when they beat Fly' },
  { value: 'gpuFirst', label: 'Every live session on a GPU' },
];
export const STRATEGY_SUB: Record<Strategy, string> = {
  demand: 'A pod starts when the sessions it would take cost more on Fly, and drains when they no longer do.',
  gpuFirst: 'A live session with no free slot starts on Fly without voices while the first GPU RunPod has boots; a pod dearer than the line is swapped for a cheaper one, live sessions handed over seamlessly; files take a free slot or Fly and give their slot to a live session; an empty pod goes at once.',
};
export interface BalancerConfig {
  enabled: boolean; strategy?: Strategy; priceLineGpu?: string; minSavingPerH?: number; flyReadyS?: number; mergeWindowS?: number; flyMultilingualPerH: number; flyEnglishPerH: number; flyPool: number; gpuTypes: GpuType[];
  gpus: Gpu[]; gpuChoice: 'order' | 'cost'; dataCenters: string[]; fallbackDataCenters: string[]; maxRttMs: number;
  refusedCooldownS: number; badGpuFailures: number; badGpuWindowS: number; stockMaxAgeS: number; podMemory: PodMemory;
  maxPods: number; minPods: number; spendCapPerDay: number; keepStopped: number; diskPerGbMonth: number;
  settleS: number; startWindowS: number; startMargin: number; drainMargin: number; drainWindowS: number; drainGraceS: number;
  idleStopS: number; speechIdleS?: number;
  speechMachines?: { enabled: boolean; maxPerH: number; readyTimeoutS: number; heartbeatTimeoutS: number; retryS: number; racers?: number; bootCapS?: number; runpod?: boolean; gpus?: { name: string; speed: number }[]; most?: number; linesPerMin?: number };
  maxFirstPerH?: number; speechBacklogS?: number; speechLineS?: number;
  speechPodHPerH?: number | null; speechPodHPerDay?: number | null;
  fileWindowS: number; fileWaitS: number; expectedReadyS: number; expectedResumeS: number;
  containerTimeoutS: number; readyTimeoutS: number; badGpuCooldownS: number; maxMovesPerPod: number; moveCooldownS: number; heartbeatTimeoutS: number; flyHeadroomShare: number; rejectPressureS: number; startOnNew: boolean; startWindowScales: boolean; podBilledWhileCreating: boolean;
  pauseFiles: boolean; pauseMaxPerFile: number; pauseMinLeftS: number; pauseMaxDone: number; pauseMaxWaitS: number; liveWaitS: number; fileAudioPerEngineS: number;
}
export interface Traffic { generate: boolean; arrivalsPerH: number; dayCurve: number[]; liveShare: number; englishShare: number; liveMedianMin: number; liveSigma: number; fileAudioMedianMin: number; fileSigma: number; fileSpeed: number; voicesPerLive?: number; voiceLinesPerMin?: number }
export interface World {
  flyBootS: number; flyIdleTailS: number; containerS: number; containerJitterS: number; engineBootS: number; engineBootJitterS: number;
  brokenHostP: number; resumeContainerS: number; resumeBootS: number; resumeNoGpuP: number; crashPerPodH: number;
  moveS: number; rescueS: number; moveFailP: number; heartbeatS: number; gpuFileSpeedup: number;
  pauseRedoS: number; pauseHandoverS: number;
  gpus: WorldGpu[];
  dataCenters: WorldDc[];
  stockEveryS: number;
  vastFreeP?: number; vastBootS?: number | null; vastBootJitterS?: number; vastGpu?: string; vastPerH?: number | null;
  speechLanes?: number; speechLinesPerMin?: number; speechGpus?: WorldSpeech[]; podSpeechLinesPerMin?: number;
}
export interface WorldSpeech { gpu: string; linesPerMin: number }
export interface WorldGpu { name: string; freeP: number; brokenHostP: number | null; bootScale: number }
export interface WorldDc { id: string; freeP: number; rttMs: number }
export interface Pricing { livePerH: number; filePerH: number; minBilledS: number }
export interface SimConfig { balancer: BalancerConfig; traffic: Traffic; world: World; pricing: Pricing; seed: number; decideEveryS: number; check: boolean }

export type Command =
  | { cmd: 'kill'; pod: number }
  | { cmd: 'rush'; n: number; lang: Lang; kind: Kind }
  | { cmd: 'arrive'; kind: Kind; lang: Lang; lifeS?: number | null; workS?: number | null; ext?: string | null }
  | { cmd: 'end'; ext: string }
  | { cmd: 'remove'; n: number; kind?: Kind | null; lang?: Lang | null }
  | { cmd: 'endSession'; session: number }
  | { cmd: 'balancer'; config: BalancerConfig }
  | { cmd: 'traffic'; traffic: Traffic }
  | { cmd: 'world'; world: World }
  | { cmd: 'pricing'; pricing: Pricing }
  | { cmd: 'speech'; seconds: number }
  | { cmd: 'reading'; lines: number };

export interface Timed { t: number; command: Command }
export interface Scenario { version: number; source: string; label: string; t0Unix: number | null; config: SimConfig; timeline: Timed[]; endT: number; fresh?: boolean }

export type Action =
  | { do: 'placeOnFly'; session: number }
  | { do: 'placeOnPod'; session: number; pod: number }
  | { do: 'wait'; session: number; pod: number | null }
  | { do: 'reject'; session: number }
  | { do: 'createPod'; pod: number; gpu: string; gpus?: string[]; dataCenters?: string[] }
  | { do: 'startSpeechMachine'; pod: number; maxPerH: number }
  | { do: 'resumePod' | 'drain' | 'undrain' | 'stopPod' | 'terminatePod'; pod: number }
  | { do: 'move'; session: number; from: Place; to: Place; emergency: boolean }
  | { do: 'pause'; session: number; from: Place; forSession: number };
export interface Decision { at: number; action: Action; why: string }

export interface Demand { overflow: number; breakEven: number; startAt: number; drainAt: number; spare: number; movable: number; highFor: number; startIn: number | null; blocked: string | null; pressure: string | null; speech?: boolean; speechCapped?: string | null }
export interface Metrics {
  sessions: number; ended: number; rejected: number; dropped: number; moves: number; rescues: number; movesFailed: number;
  podsCreated: number; podsResumed: number; startsFailed: number; crashes: number; costFly: number; costGpu: number; costDisk: number; baseline: number;
  waitSumS: number; waitN: number; waitMaxS: number; gapS: number; gapMaxS: number; revenue: number; baselineRevenue: number;
  queuedS: number; queuedSessions: number; queuedMaxS: number; podWaitS: number; podWaitSessions: number; bootingS: number; peakPods: number; peakSessions: number; violations: number; firstViolations: string[];
  pauses?: number; resumes?: number; pausedS?: number; pausedMaxS?: number; liveWaits?: number;
  byGpu?: Record<string, GpuMetrics>; refused?: Record<string, number>;
  voiceLines?: number; voiceMade?: number; voiceLagMaxS?: number; readingLines?: number;
}
export interface GpuMetrics { created: number; failed: number; ready: number; containerS: number; readyS: number; upS: number; cost: number; sessions: number }
export interface Sample { t: number; onFly: number; onGpu: number; waiting: number; pods: number; slots: number; overflow: number; startAt: number; drainAt: number; costRate: number; baselineRate: number;
  flyMachines?: number; runpodPods?: number; speechPods?: number; vastMachines?: number;
  voiceWantedPerMin?: number; voiceMadePerMin?: number; voiceWaiting?: number; voiceLagS?: number; readingLeft?: number;
}
export interface SnapSession { id: number; ext: string | null; kind: Kind; lang: Lang; place: Place; movingTo: Place | null; emergency: boolean; served: boolean; ageS: number; paused?: boolean; pauses?: number }
export interface SnapPod { id: number; gpu: string; slots: number; state: PodState; crashed: boolean; broken: boolean; used: number; stateForS: number; progress: number; resumed: boolean; perH: number; placed?: string | null; dataCenter?: string | null; speechBusy?: number; speechLanes?: number; speechOnly?: boolean; host?: 'runpod' | 'vast'; speechPerMin?: number }
export interface VoiceSnap { wantedPerMin: number; madePerMin: number; liveWaiting: number; readingLeft: number; lagS: number; canPerMin: number; speakers: number; borrowed: boolean }
export interface Snapshot {
  t: number; sessions: SnapSession[]; pods: SnapPod[]; flyPool: number; flyTail: number; demand: Demand; spendToday: number; metrics: Metrics;
  log: Decision[]; series: Sample[]; config: SimConfig; source: string; t0Unix: number | null; recorded: number; queued: number;
  voice?: VoiceSnap; speechRates?: Record<string, number>;
}

interface Exports {
  memory: WebAssembly.Memory;
  alloc(len: number): number;
  out_ptr(): number;
  out_len(): number;
  sim_init(p: number, l: number): number;
  sim_replay(p: number, l: number): number;
  sim_command(p: number, l: number): number;
  sim_export(p: number, l: number): void;
  sim_defaults(): void;
  sim_run(s: number): void;
  sim_now(): number;
  sim_snapshot(n: number): void;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export class SimEngine {
  private x: Exports;
  private constructor(x: Exports) { this.x = x; }

  static async load(): Promise<SimEngine> {
    const res = await fetch(wasmUrl);
    if (!res.ok) throw new Error(`balancer.wasm: ${res.status}`);
    const { instance } = await WebAssembly.instantiate(await res.arrayBuffer(), {});
    return new SimEngine(instance.exports as unknown as Exports);
  }

  private put(s: string): [number, number] {
    const b = enc.encode(s);
    const p = this.x.alloc(b.length);
    new Uint8Array(this.x.memory.buffer, p, b.length).set(b);
    return [p, b.length];
  }
  private out(): string { return dec.decode(new Uint8Array(this.x.memory.buffer, this.x.out_ptr(), this.x.out_len())); }
  private check(code: number) { if (code === 1) throw new Error(this.out()); if (code === 2) throw new Error('no simulation'); }

  defaults(): SimConfig { this.x.sim_defaults(); return JSON.parse(this.out()); }
  init(cfg: SimConfig) { this.check(this.x.sim_init(...this.put(JSON.stringify(cfg)))); }
  replay(sc: Scenario) { this.check(this.x.sim_replay(...this.put(JSON.stringify(sc)))); }
  command(c: Command) { this.check(this.x.sim_command(...this.put(JSON.stringify(c)))); }
  now(): number { return this.x.sim_now(); }
  run(seconds: number) { if (seconds >= 1) this.x.sim_run(Math.floor(seconds)); }
  snapshot(logN = 150): Snapshot { this.x.sim_snapshot(logN); return JSON.parse(this.out()); }
  exportText(label: string): string { this.x.sim_export(...this.put(label)); return this.out(); }
}
